import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function CharitableLegacyPlanning() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="charitable-legacy-planning" />
          {/* Header */}
          <div className="text-center mb-12">
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Charitable Legacy Planning</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Make a Lasting Impact: Integrating Philanthropy into Your Estate Plan
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is It? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Charitable Legacy Planning?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Charitable legacy planning involves incorporating philanthropic goals into your overall estate plan. It's about structuring your assets and using specific financial tools to make meaningful gifts to charities or causes you care about, often upon your death, in a way that is both impactful for the charity and tax-efficient for your estate.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                Life insurance is a powerful and popular tool for charitable legacy planning, allowing individuals to make a much larger future gift than they might be able to donate during their lifetime, often with significant tax advantages for their estate.
              </Text>
            </Card>

            {/* How It Works (Using Life Insurance) */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Leveraging Life Insurance for Charitable Giving</Title>
              <Text className="text-gray-600 dark:text-gray-300 mb-3">There are several ways to use life insurance for planned charitable gifts:</Text>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Naming a Charity as Beneficiary:</strong> The simplest method. You own the policy and name the charity as the sole or partial beneficiary. Upon death, the charity receives the death benefit proceeds directly, bypassing your estate and probate. Your estate receives a donation receipt for the death benefit amount, which can create a significant tax credit to offset estate taxes.
                </li>
                <li>
                  <strong>Gifting an Existing Policy:</strong> You irrevocably transfer ownership of an existing policy (with cash value) to a charity. You receive an immediate donation receipt for the policy's fair market value (usually close to the cash surrender value minus any policy loans). If you continue paying premiums, you receive donation receipts for those payments too.
                </li>
                <li>
                  <strong>Charity Purchases a New Policy:</strong> A charity applies for and owns a new policy on your life. You donate funds to the charity annually to cover the premium payments, and you receive donation receipts for these cash donations. The charity receives the death benefit upon your passing.
                </li>
                 <li>
                  <strong>Using Life Insurance within a Charitable Remainder Trust (CRT):</strong> A more complex strategy where assets are placed in a trust, providing income to you or beneficiaries for a term, with the remainder going to charity. Insurance can sometimes be used within or alongside CRTs to replace the value of the gifted asset for heirs.
                </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Benefits of Planned Charitable Giving with Insurance</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Magnified Gift:</strong> Allows you to leave a significantly larger donation than might be possible through outright cash gifts during your lifetime, leveraging relatively small premium payments into a large future benefit.</li>
                <li><strong>Tax Advantages for Estate:</strong> The donation receipt generated by the death benefit can create substantial tax credits to reduce or eliminate final income taxes payable by the estate (e.g., capital gains tax on deemed disposition).</li>
                <li><strong>Tax Advantages During Life (Policy Gift):</strong> Gifting a policy can generate immediate donation receipts, providing tax relief during your lifetime.</li>
                <li><strong>Certainty and Privacy:</strong> The gift passes directly to the charity outside the Will (if named beneficiary), ensuring privacy and avoiding probate fees on the insurance proceeds.</li>
                <li><strong>Legacy Creation:</strong> Creates a lasting legacy and supports causes important to you in a substantial way.</li>
                 <li><strong>Budget-Friendly:</strong> Allows philanthropy through manageable, regular premium payments rather than a large lump sum.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Important Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Factors to keep in mind:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Irrevocability (Policy Gift):</strong> Gifting ownership of a policy is generally an irrevocable decision.</li>
                <li><strong>Charity's Insurable Interest:</strong> Depending on the structure, the charity may need to demonstrate an insurable interest in the donor.</li>
                <li><strong>Premium Payments:</strong> Ensuring premiums are consistently paid is crucial for the plan's success.</li>
                <li><strong>Complexity:</strong> Some strategies, like involving trusts, require specialized legal and financial advice.</li>
                <li><strong>Integration with Overall Plan:</strong> Charitable goals should align with overall financial and estate planning objectives, ensuring family needs are also met.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is Charitable Legacy Planning Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">This approach is often appealing for individuals who:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Have strong philanthropic intentions and wish to make a significant impact.</li>
                <li>Are looking for tax-efficient ways to structure their estate and reduce final tax liabilities.</li>
                <li>Have the capacity to fund insurance premiums or gift existing policies.</li>
                <li>Want to create a lasting legacy beyond their lifetime.</li>
                <li>May have assets (like private company shares or real estate) that will trigger large capital gains taxes upon death.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Amplify Your Impact, Secure Your Legacy</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Discover how strategic charitable planning using life insurance can help you achieve your philanthropic goals while potentially providing significant tax benefits for your estate. Let's craft a legacy plan that reflects your values.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Discuss Charitable Giving Strategies
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
         <BookingWidget onClose={() => setShowBookingWidget(false)} />
      )}
    </MarketingLayout>
  );
}